import React, { useState } from 'react';
import { locationsApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Globe, Info, X } from 'lucide-react';

const WorldbuildingPanel = ({ bookId, locations, setLocations }) => {
    const [showNew, setShowNew] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [newLoc, setNewLoc] = useState({ name: '', description: '', history: '', culture: '', geography: '' });

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const response = await locationsApi.create({ ...newLoc, book_id: bookId });
            setLocations([...locations, response.data]);
            setShowNew(false);
            setNewLoc({ name: '', description: '', history: '', culture: '', geography: '' });
            toast.success('Location created');
        } catch (error) {
            toast.error('Failed to create location');
        }
    };

    return (
        <div className="p-8">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <h2 className="font-serif text-2xl font-semibold">Worldbuilding</h2>
                    <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setShowInfo(!showInfo)}
                        data-testid="worldbuilding-info-btn"
                    >
                        <Info className="h-4 w-4 text-muted-foreground" />
                    </Button>
                </div>
                <Dialog open={showNew} onOpenChange={setShowNew}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-location-btn">
                            <Plus className="h-4 w-4 mr-2" /> New Location
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>New Location</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleCreate} className="space-y-4 mt-4">
                            <div className="space-y-2">
                                <Label>Name</Label>
                                <Input value={newLoc.name} onChange={(e) => setNewLoc({...newLoc, name: e.target.value})} required />
                            </div>
                            <div className="space-y-2">
                                <Label>Description</Label>
                                <Textarea value={newLoc.description} onChange={(e) => setNewLoc({...newLoc, description: e.target.value})} />
                            </div>
                            <div className="space-y-2">
                                <Label>History</Label>
                                <Textarea value={newLoc.history} onChange={(e) => setNewLoc({...newLoc, history: e.target.value})} />
                            </div>
                            <div className="flex justify-end gap-3">
                                <Button type="button" variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
                                <Button type="submit">Create Location</Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Worldbuilding Explanation Card */}
            {showInfo && (
                <div className="mb-6 p-5 rounded-xl border border-accent/30 bg-accent/5" data-testid="worldbuilding-info-card">
                    <div className="flex items-start gap-4">
                        <div className="p-2 rounded-lg bg-accent/20">
                            <Globe className="h-5 w-5 text-accent" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-serif font-semibold text-lg mb-2">What is Worldbuilding?</h3>
                            <p className="text-sm text-muted-foreground mb-3">
                                Worldbuilding is the process of creating the fictional setting where your story takes place. 
                                It includes everything from geography and climate to cultures, languages, history, and social systems.
                            </p>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <div className="p-3 rounded-lg bg-background/50">
                                    <div className="font-medium text-sm mb-1">Physical World</div>
                                    <p className="text-xs text-muted-foreground">
                                        Geography, climate, cities, landmarks, flora & fauna, technology level
                                    </p>
                                </div>
                                <div className="p-3 rounded-lg bg-background/50">
                                    <div className="font-medium text-sm mb-1">Social World</div>
                                    <p className="text-xs text-muted-foreground">
                                        Cultures, religions, governments, economies, social hierarchies, customs
                                    </p>
                                </div>
                                <div className="p-3 rounded-lg bg-background/50">
                                    <div className="font-medium text-sm mb-1">Historical Context</div>
                                    <p className="text-xs text-muted-foreground">
                                        Major events, wars, discoveries, myths & legends, how the world got here
                                    </p>
                                </div>
                                <div className="p-3 rounded-lg bg-background/50">
                                    <div className="font-medium text-sm mb-1">Rules & Systems</div>
                                    <p className="text-xs text-muted-foreground">
                                        Magic systems, science rules, laws, what's possible and impossible
                                    </p>
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground mt-3 italic">
                                Tip: Start with what's relevant to your story. You don't need to build everything—focus on details that affect your characters and plot.
                            </p>
                        </div>
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 -mt-1 -mr-1"
                            onClick={() => setShowInfo(false)}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {locations.map((loc) => (
                    <div key={loc.id} className="card-default p-4" data-testid={`location-${loc.id}`}>
                        <h3 className="font-serif font-semibold mb-2">{loc.name}</h3>
                        {loc.description && (
                            <p className="text-sm text-muted-foreground line-clamp-3">{loc.description}</p>
                        )}
                        {loc.history && (
                            <div className="mt-3">
                                <span className="label-text text-accent">History:</span>
                                <p className="text-sm text-muted-foreground line-clamp-2">{loc.history}</p>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            
            {locations.length === 0 && (
                <div className="text-center py-12">
                    <Globe className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                    <h3 className="font-serif font-semibold mb-1">No locations yet</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                        Start building your world by adding locations, settings, and places.
                    </p>
                    <Button variant="outline" onClick={() => setShowNew(true)}>
                        <Plus className="h-4 w-4 mr-2" /> Add Your First Location
                    </Button>
                </div>
            )}
        </div>
    );
};

export default WorldbuildingPanel;
